import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchJson, uploadJson, type UploadProgress } from "@/lib/api";
import { NOTIFICATIONS_KEY } from "@/hooks/use-notifications";

export type BugReportStatus = "otvoren" | "rijesen";
export type BugReportType = "bug" | "prijedlog";

export type BugReportAttachment = {
  id: string;
  fileName: string;
  contentType: string;
  size: number;
};

export type BugReport = {
  id: string;
  type: BugReportType;
  title: string;
  description: string;
  pageUrl: string;
  status: BugReportStatus;
  reportedBy: string;
  reportedById: string;
  resolvedBy: string;
  resolvedAt: number | null;
  createdAt: number;
  attachments: BugReportAttachment[];
  messageCount: number;
};

export type BugReportMessage = {
  id: string;
  authorId: string;
  authorName: string;
  body: string;
  createdAt: number;
};

export type BugReportThread = BugReport & { messages: BugReportMessage[] };

const threadKey = (id: string | null) => ["bugReportThread", id] as const;

export function useBugReports(
  status: BugReportStatus | "sve",
  type: BugReportType | "sve",
  page = 1,
) {
  const params = new URLSearchParams({ page: String(page), per_page: "25" });
  if (status !== "sve") params.set("status", status);
  if (type !== "sve") params.set("type", type);

  return useQuery({
    queryKey: ["bugReports", status, type, page],
    queryFn: () =>
      fetchJson<{ items: BugReport[]; total: number; counts: Record<BugReportStatus, number> }>(
        `/api/bug-reports?${params}`,
      ),
    // Lista ostaje na ekranu dok stiže nova strana/filter — bez treptanja skeletona.
    placeholderData: (prev) => prev,
  });
}

/** Da li server ima R2 za priloge — bez njega dijalog ne nudi dodavanje fajlova. */
export function useBugReportConfig() {
  return useQuery({
    queryKey: ["bugReportConfig"],
    queryFn: () => fetchJson<{ attachmentsEnabled: boolean }>("/api/bug-reports/config"),
    // Mijenja se samo s env varijablama servera, nema potrebe za ponovnim dohvatom.
    staleTime: Infinity,
  });
}

export function useReportBug() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      files,
      onProgress,
      signal,
      ...body
    }: {
      type: BugReportType;
      title: string;
      description: string;
      pageUrl: string;
      files?: File[];
      /** Napredak uploada — javlja se samo kad prijava ima priloge. */
      onProgress?: (p: UploadProgress) => void;
      signal?: AbortSignal;
    }) => {
      if (!files || files.length === 0) {
        return fetchJson<BugReport>("/api/bug-reports", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          signal,
        });
      }
      // Multipart: Content-Type (s boundaryjem) postavlja browser sam.
      const form = new FormData();
      form.set("type", body.type);
      form.set("title", body.title);
      form.set("description", body.description);
      form.set("pageUrl", body.pageUrl);
      for (const f of files) form.append("files", f, f.name);
      return uploadJson<BugReport>("/api/bug-reports", form, { onProgress, signal });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bugReports"] }),
  });
}

export function useSetBugReportStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: BugReportStatus }) =>
      fetchJson<BugReport>(`/api/bug-reports/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["bugReports"] });
      qc.invalidateQueries({ queryKey: threadKey(id) });
    },
  });
}

/**
 * Tiket s porukama. Server pri otvaranju označi pročitanim korisnikove notifikacije
 * za ovaj tiket, pa se zvonce osvježava nakon svakog dohvata.
 */
export function useBugReportThread(id: string | null) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: threadKey(id),
    queryFn: async () => {
      const thread = await fetchJson<BugReportThread>(`/api/bug-reports/${id}`);
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      return thread;
    },
    enabled: id !== null,
    // Dok je dijalog otvoren, odgovor druge strane stiže bez ručnog osvježavanja.
    refetchInterval: 60_000,
  });
}

export function useSendBugReportMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      fetchJson<BugReportMessage>(`/api/bug-reports/${id}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body }),
      }),
    onSuccess: (message, { id }) => {
      // Poruka se odmah dodaje u razgovor; ponovni dohvat samo potvrđuje redoslijed.
      qc.setQueryData<BugReportThread>(threadKey(id), (prev) =>
        prev && !prev.messages.some((m) => m.id === message.id)
          ? { ...prev, messages: [...prev.messages, message], messageCount: prev.messageCount + 1 }
          : prev,
      );
      qc.invalidateQueries({ queryKey: threadKey(id) });
      // Broj poruka u listi prijava.
      qc.invalidateQueries({ queryKey: ["bugReports"] });
    },
  });
}

export function useDeleteBugReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetchJson<void>(`/api/bug-reports/${id}`, { method: "DELETE" }),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["bugReports"] });
      qc.removeQueries({ queryKey: threadKey(id) });
      // Server briše i notifikacije obrisane prijave.
      qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}
